/* Hero Cloud behaviour.
   Layers move on separate parallax depths, opposite to the mouse:
   cloud (subtle) < copy (barely) < posters (deepest). Parallax writes to
   the layer element, the idle drift writes to a nested wrapper — the two
   never fight over one transform. Clicking a poster adds its first
   available variant via the AJAX Cart API; feedback = green glow +
   "[ ADDED TO BAG ]" chip. Requires gsap.min.js (vendored). */
(function () {
  'use strict';

  var CART_ROOT =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function setupCart(root) {
    var labels = {
      hint: root.dataset.labelHint || '[ TAP TO ADD ]',
      added: root.dataset.labelAdded || '[ ADDED TO BAG ]',
      soldout: root.dataset.labelSoldout || '[ SOLD OUT ]',
      error: root.dataset.labelError || '[ TRY AGAIN ]'
    };

    root.querySelectorAll('[data-hcl-add]').forEach(function (btn) {
      var chip = btn.querySelector('[data-hcl-chip]');

      if (btn.dataset.available !== 'true') {
        btn.classList.add('is-soldout');
        btn.setAttribute('aria-disabled', 'true');
        if (chip) chip.textContent = labels.soldout;
        return;
      }

      btn.addEventListener('click', function () {
        if (btn.classList.contains('is-busy') || btn.classList.contains('is-soldout')) return;
        btn.classList.add('is-busy');

        fetch(CART_ROOT + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: Number(btn.dataset.variantId),
            quantity: 1
          })
        })
          .then(function (res) {
            if (!res.ok) throw new Error('cart add failed: ' + res.status);
            return res.json();
          })
          .then(function (item) {
            if (chip) chip.textContent = labels.added;
            btn.classList.add('is-added');
            document.dispatchEvent(
              new CustomEvent('valismo:cart:added', { detail: item })
            );
          })
          .catch(function () {
            if (chip) chip.textContent = labels.error;
            btn.classList.add('is-error');
          })
          .then(function () {
            setTimeout(function () {
              if (chip) chip.textContent = labels.hint;
              btn.classList.remove('is-added', 'is-error', 'is-busy');
            }, 1600);
          });
      });
    });
  }

  function setupMotion(root) {
    if (!window.gsap) return null;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;

    var cloud = root.querySelector('[data-hcl-cloud]');
    var drift = root.querySelector('[data-hcl-drift]');
    var copy = root.querySelector('[data-hcl-copy]');
    var posters = Array.prototype.slice.call(
      root.querySelectorAll('[data-hcl-poster]')
    );
    var floats = Array.prototype.slice.call(
      root.querySelectorAll('[data-hcl-float]')
    );

    var ctx = gsap.context(function () {
      /* take over the CSS translate(-50%,-50%) centering with percent-based
         GSAP transforms: quickTo x/y writes px on top of xPercent/yPercent,
         so the centering survives every parallax move. (The CSS transform
         stays as the no-JS / reduced-motion fallback.) */
      gsap.set([copy].concat(posters).filter(Boolean), {
        xPercent: -50,
        yPercent: -50,
        x: 0,
        y: 0
      });

      /* idle life: the cloud breathes slowly, the posters bob like they
         hang in the air — runs with or without a mouse (also on touch) */
      if (drift) {
        gsap.fromTo(
          drift,
          { scale: 1.02, x: -8 },
          { scale: 1.05, x: 8, duration: 16, ease: 'sine.inOut', yoyo: true, repeat: -1 }
        );
      }
      floats.forEach(function (el, i) {
        gsap.to(el, {
          y: i % 2 === 0 ? 9 : -9,
          rotation: i % 2 === 0 ? 0.6 : -0.6,
          duration: 4.4 + i * 0.9,
          ease: 'sine.inOut',
          yoyo: true,
          repeat: -1
        });
      });

      /* mouse parallax (fine pointers): every layer eases against the
         cursor at its own depth */
      if (!window.matchMedia('(pointer: fine)').matches) return;

      var to = [];
      if (cloud) {
        to.push({
          x: gsap.quickTo(cloud, 'x', { duration: 1.1, ease: 'power3.out' }),
          y: gsap.quickTo(cloud, 'y', { duration: 1.1, ease: 'power3.out' }),
          depthX: -22,
          depthY: -14
        });
      }
      if (copy) {
        to.push({
          x: gsap.quickTo(copy, 'x', { duration: 1.2, ease: 'power3.out' }),
          y: gsap.quickTo(copy, 'y', { duration: 1.2, ease: 'power3.out' }),
          depthX: -9,
          depthY: -6
        });
      }
      posters.forEach(function (p) {
        to.push({
          x: gsap.quickTo(p, 'x', { duration: 0.9, ease: 'power3.out' }),
          y: gsap.quickTo(p, 'y', { duration: 0.9, ease: 'power3.out' }),
          depthX: -42,
          depthY: -28
        });
      });

      /* copy is centered via CSS translate(-50%,-50%); GSAP x/y are
         additive transforms, so the centering survives */
      function onMove(event) {
        var r = root.getBoundingClientRect();
        var nx = (event.clientX - r.left) / r.width - 0.5;
        var ny = (event.clientY - r.top) / r.height - 0.5;
        to.forEach(function (t) {
          t.x(nx * t.depthX);
          t.y(ny * t.depthY);
        });
      }

      function onLeave() {
        to.forEach(function (t) {
          t.x(0);
          t.y(0);
        });
      }

      root.addEventListener('mousemove', onMove, { passive: true });
      root.addEventListener('mouseleave', onLeave);
    }, root);

    return ctx;
  }

  function init(root) {
    if (!root || root.dataset.hclInit) return;
    root.dataset.hclInit = '1';
    setupCart(root);
    root._hclCtx = setupMotion(root);
  }

  function destroy(root) {
    if (!root) return;
    if (root._hclCtx) {
      root._hclCtx.revert();
      root._hclCtx = null;
    }
    delete root.dataset.hclInit;
  }

  function initAll() {
    document.querySelectorAll('[data-hero-cloud]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-hero-cloud]');
    if (root) init(root);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('[data-hero-cloud]');
    if (root) destroy(root);
  });
})();
